import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { toUsdStr } from 'pared/utils/number'

import { useDateFilter } from '../../../../dateFilter'
import { useGroupFilter } from '../../../../groupFilter'
import { IApiType } from '../../../types'

const QUERY_NAME = 'listLocationMetricValues'
const query = gql`
  query (
    $iStartDate: Date!
    $iEndDate: Date!
    $iFilter: JSON!
  ) {
    ${QUERY_NAME}(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) {
      nodes {
        locationId
        metricData
      }
    }
  }
`

export const fwWingstopLocationLaborOverviewConfigs = {
  name: 'string',
  value: 'string',
} as const

const useLocationLaborOverview = (): IApiType => {
  const { startDate, endDate } = useDateFilter()
  const { groupFilter } = useGroupFilter()

  const iFilter = {
    location_ids: groupFilter?.ids,
    metrics: ['labor_actual_percent', 'ot_actual_hours'],
  }

  const { data, loading } = useQuery(query, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iFilter,
    },
    skip: !startDate || !endDate || !groupFilter,
  })

  return {
    data: useMemo(() => {
      const nodes = data?.[QUERY_NAME].nodes as
        | {
            locationId: string
            metricData: {
              [key: string]: {
                name: string
                unit: string
                value: number
              }
            }
          }[]
        | undefined

      if (!nodes || nodes.length === 0) return null
      const mappedData: {
        [labor: string]: {
          [kpi: string]: number | string
        }
      } = {}
      const metricData = nodes[0]?.metricData

      const hourlyLaborPercent =
        metricData['labor_actual_percent']?.value * 100.0
      const overtimeHours = metricData['ot_actual_hours']?.value

      mappedData['hourlyLaborPercent'] = {
        name: 'Hourly Labor %',
        value: `${
          hourlyLaborPercent
            ? [hourlyLaborPercent.toFixed(2), '%'].join('')
            : '-'
        }`,
      }

      mappedData['overtimeCost'] = {
        name: 'Overtime',
        value: `${
          overtimeHours ? [overtimeHours.toFixed(1), 'hours'].join(' ') : '-'
        }`,
      }

      const rows = Object.values(mappedData).map((n) => {
        return {
          ...n,
          id: n.name as string,
          parentId: 'root',
        }
      })

      return rows
    }, [groupFilter, data]),
    loading,
  }
}

export default useLocationLaborOverview
